import { containsPii, clone, getWebView, getDeviceType } from '../../lib/utils';
import Logger from '../../lib/logger';
import { PAGEVIEW_KEY } from '../../lib/pageview_counter';

export function ensureVariableIsString(variable) {
  /**
   * Ensures variables are converted to strings for third-party services that require this
   * (GAM requires this for valid targeting key-values)
   * @param  {String} variable Targeting parameter value or list of values.
   */
  return Array.isArray(variable) ? variable.map(v => String(v)) : String(variable);
}

export function variablesMixin(ConcertAds) {
  /**
   * Publishes custom variables to third-party services (Google)
   * @param  {String} key      Targeting parameter key.
   * @param  {String} variable Targeting parameter value or list of values.
   */
  ConcertAds.prototype.addVariable = function(key, variable) {
    // Avoid setting variables that look like PII
    // Currently checks for things that look like email addresses.
    // See: https://support.google.com/adsense/topic/6162392
    if (containsPii(variable)) return false;

    this.variables[key] = variable;
    setDFPTargeting({ key, variable });

    // For tests
    return true;
  };

  /**
   * Remove a custom variable and clear it from third-party services (Google)
   * @param  {String} key      Targeting parameter key.
   */
  ConcertAds.prototype.removeVariable = function(key) {
    delete this.variables[key];
    clearDFPTargeting(key);
  };

  /**
   * Mark the page as sponsored (or not), mirroring Atlas MG's SponsorAPI: sponsored pages carry
   * `sponsored=yes` page targeting, and plugins such as Browsi skip themselves. Overrides the
   * `sponsored` setting for the rest of the page.
   * @param  {Boolean} [isSponsored=true]
   */
  ConcertAds.prototype.setSponsored = function(isSponsored = true) {
    this.sponsored = Boolean(isSponsored);

    if (this.sponsored) {
      this.addVariable('sponsored', 'yes');
    } else {
      this.removeVariable('sponsored');
    }
  };

  /**
   * Whether the page is sponsored: the last setSponsored() call, else the `sponsored` setting.
   * @return {Boolean}
   */
  ConcertAds.prototype.isSponsored = function() {
    return this.sponsored ?? Boolean(this.settings.sponsored);
  };

  /**
   * Getter for all available ConcertAds variables
   * @param  {String} key      Targeting parameter key to get.
   * @return {String}          Returns value of key or false
   */
  ConcertAds.prototype.getVariable = function(key) {
    return this.getAllVariables()[key];
  };

  /**
   * Getter for all available ConcertAds variables
   * @param  {String} key      Targeting parameter key to get.
   * @return {String}          Returns value of key or false
   */
  ConcertAds.prototype.getAllVariables = function() {
    return clone(this.variables);
  };
}

export function setUpInitialVariables(app) {
  app.variables = {};
  clearDFPTargeting();

  /**
   * Set "webview" variable.
   */
  let webview = getWebView(window.navigator.userAgent);
  if (webview) {
    app.addVariable('webview', webview);
  }

  /**
   * Set "device_type" variable.
   */
  app.addVariable('device_type', getDeviceType());

  /**
   * ADS-65 — session pageview count. Incremented once in `_init`; republish the
   * same value on reinstall (virtual PVs must not bump, matching Atlas).
   */
  app.addVariable(PAGEVIEW_KEY, app.pageview || '1');

  /**
   * Set "sponsored" variable, as Atlas MG's SponsorAPI did.
   */
  if (app.isSponsored()) {
    app.addVariable('sponsored', 'yes');
  }

  /**
   * Set any variables passed as constructor options, which take priority
   * over derived variables.
   */
  let constructorVars = app.settings.dfpVariables || {};
  Object.keys(constructorVars).forEach(key => {
    app.addVariable(key, constructorVars[key]);
  });

  /**
   * Set any variables from `dfp_key=key:value` URL parameters. These run last
   * so they override any matching constructor `dfpVariables` values.
   */
  setDfpKvpQueryOverrides(app);
}

/**
 * Publish variables from `dfp_key=key:value` URL parameters.
 *
 * Reads every `dfp_key` parameter from the query string and hash fragment of
 * the current URL, splits each value on its first colon, and publishes the
 * pair straight to `app.addVariable`. Entries with no colon or an empty key
 * are ignored. `getAll()` decodes each value, so `dfp_key=k%3Av` resolves to
 * `k:v`.
 *
 * @param {ConcertAds} app
 */
function setDfpKvpQueryOverrides(app) {
  const { search = '', hash = '' } = window.location;

  // A hash can carry its own query string, e.g. `#/route?dfp_key=…`.
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?')) : hash.replace(/^#/, '');

  const rawValues = [
    ...new URLSearchParams(search).getAll('dfp_key'),
    ...new URLSearchParams(hashQuery).getAll('dfp_key'),

    // @TODO: to clean up, add for backward support with existing partners support doc reference.
    ...new URLSearchParams(search).getAll('sk_dfp_key'),
    ...new URLSearchParams(hashQuery).getAll('sk_dfp_key'),
  ];

  rawValues.forEach(raw => {
    const separator = raw.indexOf(':');
    if (separator <= 0) {
      return; // no colon, or an empty key like `:value`
    }
    app.addVariable(raw.slice(0, separator), raw.slice(separator + 1));
  });
}

/**
 * Set DFP/GAM Targeting
 */
function setDFPTargeting({ key, variable }) {
  googletag.cmd.push(function() {
    googletag.pubads().setTargeting(key, ensureVariableIsString(variable));
    Logger.log(`Adding ${key} with value: ${ensureVariableIsString(variable)}`);
  });
}

/**
 * Clear existing DFP/GAM Targeting: one key, or all of it when no key is given
 * @param  {String} [key] Targeting parameter key.
 */
function clearDFPTargeting(key) {
  googletag.cmd.push(function() {
    googletag.pubads().clearTargeting(key);
  });
}
